import React, { useCallback, useMemo, useState } from "react";
import { Eventcalendar, setOptions, Popup, Select, Datepicker } from "@mobiscroll/react";
import { Typography } from "@material-ui/core";
import { formatDateHHMM } from "@icarius-utils/date";
import { getLocalizedString } from "@icarius-localization/strings";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import { toShiftDate } from "./useGenericSchedule";

const responsivePopup = {
    medium: {
        display: "center",
        width: 400,
        fullScreen: false,
        touchUi: false,
        showOverlay: false,
    },
};

setOptions({
    theme: "material",
    themeVariant: "light",
});

const Schedule = (props) => {
    const { staff, schedules, scheduleFunctions, scheduleData, showWeek, scheduleFilter, initialShifts, date } = props;

    const [viewDate, setViewDate] = useState(() => date || new Date());

    const onSelectedDateChange = useCallback((args) => {
        setViewDate(args.date);
    }, []);

    const toTime = (d) => (d instanceof Date ? d : new Date(d)).getTime();

    const schedulesFilteredFormatted = useMemo(() => {
        // encontrar el original del GET
        const originalShift = initialShifts.find((item) => {
            const temp = scheduleData.tempShift;
            if (!temp) return false;
            if (item?.resource !== temp.resource) return false;
            if (item?.type !== temp.type) return false;
            return toShiftDate(item.start)?.getTime() === toShiftDate(temp.start)?.getTime();
        });

        // encontrar el schedule del original del GET
        const originalSchedule = schedules.find((item) => item.key === originalShift?.scheduleId);

        //filtrar los del mismo grupo (si no tiene grupo, todos) y formatear
        return schedules
            .filter((item) => {
                if (item.key === "LIBRE") return true;
                if (originalSchedule?.group) return item.group === originalSchedule.group;
                return true;
            })
            .map((item) => ({ value: item.key, text: item.value }));
    }, [schedules, scheduleData.tempShift, initialShifts]);

    const viewSettings = useMemo(() => {
        return {
            timeline: {
                type: showWeek ? "week" : "month",
                eventList: true,
            },
        };
    }, [showWeek]);

    const getRemainingTime = () => {
        if (!scheduleData.extraTime) return formatDateHHMM(new Date(scheduleData.originalExtraTime));

        const start = new Date(scheduleData.originalExtraTime);
        const newHours = start.getHours() - scheduleData.extraTime.getHours();
        const newMinutes = start.getMinutes() - scheduleData.extraTime.getMinutes();
        return formatDateHHMM(new Date(0, 0, 0, newHours, newMinutes));
    };

    const staffIds = useMemo(() => new Set(staff.map((s) => s.id)), [staff]);

    const shiftsToUse = useMemo(() => {
        if (!scheduleData.shifts) return [];

        const rangeStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1 - 7).getTime();
        const rangeEnd = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 7).getTime();

        return scheduleData.shifts.filter((shift) => {
            if (shift.type === "requestRejected") return false;
            if (!staffIds.has(shift.resource)) return false;
            if (scheduleFilter && shift.scheduleId !== scheduleFilter) return false;

            return toTime(shift.start) <= rangeEnd && toTime(shift.end) >= rangeStart;
        });
    }, [scheduleData.shifts, scheduleFilter, viewDate, staffIds]);

    const extraHoursEnd = (() => {
        if (!scheduleData.extraHoursMax) return new Date(0, 0, 0, 23, 59, 0, 0);
        const [hours, minutes] = scheduleData.extraHoursMax.split(":").map(Number);
        return new Date(0, 0, 0, hours, minutes, 0, 0);
    })();

    return (
        <div style={{ marginLeft: 10, marginRight: 10 }}>
            <Typography className="whiteText" noWrap style={{ margin: 10, marginTop: 0 }}>
                {getLocalizedString("clickScheduleToChangeDoubleCkickToAuth")}
            </Typography>
            <Eventcalendar
                view={viewSettings}
                resources={staff}
                data={shiftsToUse}
                extendDefaultEvent={scheduleFunctions.extendDefaultEvent}
                onEventClick={scheduleFunctions.onEventClick}
                onEventCreate={scheduleFunctions.onEventCreate}
                onEventCreated={scheduleFunctions.onEventCreated}
                onEventDeleted={scheduleFunctions.onEventDeleted}
                renderResource={scheduleFunctions.renderMyResource}
                renderScheduleEventContent={scheduleFunctions.renderScheduleEventContent}
                onSelectedDateChange={onSelectedDateChange}
                dragToCreate={false}
                dragToResize={false}
                dragToMove={false}
                clickToCreate={true}
                cssClass="md-employee-shifts"
                height={"calc(100vh - 225px)"}
                dayNamesShort={[
                    getLocalizedString("dom"),
                    getLocalizedString("mondayShort"),
                    getLocalizedString("marchShort"),
                    getLocalizedString("wed"),
                    getLocalizedString("thu"),
                    getLocalizedString("fri"),
                    getLocalizedString("sat"),
                ]}
                defaultSelectedDate={date || new Date()}
            />
            {scheduleData.popUpIsOpen && (
                <Popup
                    isOpen={scheduleData.popUpIsOpen}
                    headerText={scheduleData.headerText}
                    onClose={scheduleFunctions.onClose}
                    buttons={scheduleFunctions.getPopupButtons()}
                    responsive={responsivePopup}
                    fullScreen={true}
                    contentPadding={false}
                    display="bottom"
                    cssClass="employee-shifts-popup"
                >
                    <div className="mbsc-form-group">
                        {scheduleData.isExtra && (
                            <>
                                <Typography className="mbsc-material mbsc-textfield-wrapper">
                                    {`${
                                        scheduleData.hasAnotherExtra
                                            ? getLocalizedString("extraHoursToAuthorize")
                                            : getLocalizedString("maxOvertimeHoursForPreauthorization")
                                    }: ${formatDateHHMM(scheduleData.originalExtraTime) || "-"}`}
                                </Typography>
                                <Datepicker
                                    label={getLocalizedString("authorizedOvertimeHours")}
                                    inputTyping={false}
                                    controls={["time"]}
                                    onChange={(e) => scheduleFunctions.handleTimeChange(e.value)}
                                    value={scheduleData.extraTime}
                                    valid={[
                                        {
                                            start: new Date(0, 0, 0, 0, 0, 0, 0),
                                            end: extraHoursEnd,
                                        },
                                    ]}
                                />
                                {scheduleData.hasAnotherExtra && (
                                    <Typography className="mbsc-material mbsc-textfield-wrapper">
                                        {`${getLocalizedString("unauthorizedOvertime")}: ${getRemainingTime()}`}
                                    </Typography>
                                )}
                            </>
                        )}
                        {scheduleData.isRequest && (
                            <Typography className="mbsc-material mbsc-textfield-wrapper">
                                {getLocalizedString("acceptScheduleChangeRequest")}
                            </Typography>
                        )}
                        {!scheduleData.isExtra && !scheduleData.isRequest && (
                            <Select
                                data={schedulesFilteredFormatted}
                                label={getLocalizedString("selectSchedule")}
                                onChange={(args) => scheduleFunctions.handleDateChange(args)}
                                value={scheduleData.schedule}
                            />
                        )}
                    </div>
                </Popup>
            )}
        </div>
    );
};

export default Schedule;
